import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Flame, Crown, ArrowRight, ChevronLeft, Trophy } from "lucide-react";

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const map = {
    live: { c: "bg-court-red text-white animate-pulse", t: t("common.live") },
    overtime: { c: "bg-orange text-white", t: t("game.overtime") },
    finished: { c: "bg-navy-surface text-ink-secondary border border-navy-border", t: t("common.final") },
    scheduled: { c: "bg-navy-surface text-ink-secondary border border-navy-border", t: t("common.upcoming") },
  };
  const s = map[status] || map.scheduled;
  return <span className={`px-2 py-1 text-[10px] font-heading uppercase tracking-[0.2em] rounded-sm ${s.c}`}>{s.t}</span>;
}

export default function TournamentPortal() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [tournament, setTournament] = useState(null);
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [metric, setMetric] = useState("pts");
  const [notFound, setNotFound] = useState(false);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const t = (await api.get(`/tournaments/by-slug/${slug}`)).data;
        if (!mounted) return;
        setTournament(t);
        const [gs, ts, ps] = await Promise.all([
          api.get(`/games?tournament_id=${t.id}`),
          api.get(`/teams?tournament_id=${t.id}`),
          api.get(`/players?tournament_id=${t.id}`),
        ]);
        if (!mounted) return;
        setGames(gs.data);
        setTeams(ts.data);
        setPlayers(ps.data);
      } catch (_) {
        if (mounted) setNotFound(true);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    if (!tournament) return;
    const qs = new URLSearchParams({ tournament_id: tournament.id, metric, limit: "15" });
    if (category !== "all") qs.set("category_id", category);
    api.get(`/leaders?${qs.toString()}`).then((r) => setLeaders(r.data));
  }, [tournament, metric, category]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto p-12 text-center">
        <div className="font-heading uppercase tracking-widest text-orange text-sm mb-2">404</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight mb-3">{t("tournament.not_found_title")}</h1>
        <p className="text-ink-secondary mb-6">{t("tournament.not_found_hint")}</p>
        <Link to="/" className="inline-flex items-center gap-1 text-orange hover:underline font-heading uppercase tracking-widest text-sm">
          <ChevronLeft size={14} /> {t("tournament.back_to_portal")}
        </Link>
      </div>
    );
  }
  if (!tournament) return <div className="max-w-7xl mx-auto p-12 text-ink-muted">{t("common.loading")}</div>;

  const teamsMap = Object.fromEntries(teams.map((t) => [t.id, t]));
  const categoryList = tournament.categories || [];
  // Per-tournament category resolver: prefer team.category_by_tournament[currentTid], fallback to legacy category_id
  const teamCategoryInTournament = (team) => {
    if (!team) return null;
    const cbt = team.category_by_tournament || {};
    if (cbt[tournament.id]) return cbt[tournament.id];
    if (team.tournament_id === tournament.id) return team.category_id || null;
    return null;
  };
  const filterByCategory = (arr, kind) => {
    if (category === "all") return arr;
    if (kind === "team") return arr.filter((x) => teamCategoryInTournament(x) === category);
    if (kind === "player") return arr.filter((p) => teamCategoryInTournament(teamsMap[p.team_id]) === category);
    if (kind === "game") {
      return arr.filter((g) => {
        return teamCategoryInTournament(teamsMap[g.team_a_id]) === category || teamCategoryInTournament(teamsMap[g.team_b_id]) === category;
      });
    }
    return arr;
  };
  const gamesF = filterByCategory(games, "game");
  const teamsF = filterByCategory(teams, "team");
  const playersF = filterByCategory(players, "player");
  const live = gamesF.filter((g) => g.status === "live" || g.status === "overtime");
  const upcoming = gamesF.filter((g) => g.status === "scheduled").slice(0, 8);
  const recent = gamesF.filter((g) => g.status === "finished").slice(0, 8);

  const bg = tournament.hero_bg_url || "https://images.unsplash.com/photo-1519861531473-9200262188bf?q=80&w=2400&auto=format&fit=crop";

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-navy-border" data-testid="tournament-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bg})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
        <div className="absolute inset-0 court-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-24">
          <Link to="/" className="inline-flex items-center gap-1 text-ink-muted hover:text-orange text-xs uppercase tracking-widest font-heading mb-6">
            <ChevronLeft size={14} /> {t("common.portal")}
          </Link>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-sm bg-navy-deep/60 border-2 border-orange grid place-items-center overflow-hidden shrink-0">
              {tournament.logo_url ? (
                <img src={fileUrl(tournament.logo_url)} alt={tournament.name} className="w-full h-full object-cover" />
              ) : (
                <span className="font-heading font-black text-6xl text-orange">{tournament.name?.[0]}</span>
              )}
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 bg-orange/15 border border-orange/40 px-3 py-1 rounded-sm mb-3">
                <Flame size={14} className="text-orange" />
                <span className="text-orange font-heading uppercase tracking-[0.3em] text-[10px] md:text-xs">{t("tournament.label")}</span>
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase tracking-tight text-white leading-[1.05]" data-testid="tournament-name">
                {tournament.name}
              </h1>
              {(tournament.hero_title || tournament.season) && (
                <div className="text-orange font-heading uppercase tracking-widest text-base md:text-lg mt-2">
                  {tournament.hero_title || tournament.season}
                </div>
              )}
              {tournament.hero_subtitle && (
                <p className="text-ink-secondary text-sm md:text-base max-w-2xl mt-3">{tournament.hero_subtitle}</p>
              )}
              <div className="flex flex-wrap gap-4 mt-4 text-xs md:text-sm font-mono uppercase tracking-widest text-ink-muted">
                <span data-testid="tournament-teams-count">{t("tournament.teams_count", { count: teamsF.length })}</span>
                <span>·</span>
                <span data-testid="tournament-players-count">{t("tournament.players_count", { count: playersF.length })}</span>
                <span>·</span>
                <span data-testid="tournament-games-count">{t("tournament.games_count", { count: gamesF.length })}</span>
                {tournament.season && <><span>·</span><span>{t("common.season")} {tournament.season}</span></>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live ticker */}
      {live.length > 0 && (
        <section className="bg-court-red/10 border-b border-court-red/30">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-4 overflow-x-auto">
            <span className="px-2 py-1 text-[10px] font-heading uppercase tracking-[0.3em] bg-court-red text-white animate-pulse rounded-sm shrink-0">LIVE</span>
            {live.map((g) => (
              <Link key={g.id} to={`/games/${g.id}`} className="text-sm font-mono tabular whitespace-nowrap text-white hover:text-orange">
                {teamsMap[g.team_a_id]?.name || "A"} <span className="text-orange">{g.score_a}</span> – <span className="text-orange">{g.score_b}</span> {teamsMap[g.team_b_id]?.name || "B"}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {categoryList.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="tournament-category-bar">
            <span className="text-[10px] font-heading uppercase tracking-widest text-ink-muted mr-2">Categoria:</span>
            <button
              type="button"
              onClick={() => setCategory("all")}
              data-testid="tournament-category-all"
              className={`px-3 py-1.5 text-xs font-heading uppercase tracking-widest rounded-sm border transition-colors ${
                category === "all" ? "bg-orange border-orange text-white" : "bg-transparent border-navy-border text-ink-secondary hover:text-white hover:border-orange"
              }`}
            >
              Todas
            </button>
            {categoryList.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                data-testid={`tournament-category-btn-${c.id}`}
                className={`px-3 py-1.5 text-xs font-heading uppercase tracking-widest rounded-sm border transition-colors ${
                  category === c.id ? "bg-orange border-orange text-white" : "bg-transparent border-navy-border text-ink-secondary hover:text-white hover:border-orange"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}

        <Tabs defaultValue="games">
          <TabsList className="bg-navy-deep border border-navy-border rounded-sm p-1 flex-wrap h-auto">
            <TabsTrigger value="games" data-testid="tourney-tab-games" className="font-heading uppercase tracking-widest text-xs data-[state=active]:bg-orange data-[state=active]:text-white rounded-sm">{t("nav.games")}</TabsTrigger>
            <TabsTrigger value="teams" data-testid="tourney-tab-teams" className="font-heading uppercase tracking-widest text-xs data-[state=active]:bg-orange data-[state=active]:text-white rounded-sm">{t("nav.teams")}</TabsTrigger>
            <TabsTrigger value="players" data-testid="tourney-tab-players" className="font-heading uppercase tracking-widest text-xs data-[state=active]:bg-orange data-[state=active]:text-white rounded-sm">{t("nav.players")}</TabsTrigger>
            <TabsTrigger value="leaders" data-testid="tourney-tab-leaders" className="font-heading uppercase tracking-widest text-xs data-[state=active]:bg-orange data-[state=active]:text-white rounded-sm">{t("nav.leaders")}</TabsTrigger>
          </TabsList>

          <TabsContent value="games" className="mt-6 space-y-8">
            {upcoming.length > 0 && (
              <div>
                <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mb-3">{t("home.upcoming")}</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {upcoming.map((g) => <GameCard key={g.id} game={g} teamsMap={teamsMap} />)}
                </div>
              </div>
            )}
            {recent.length > 0 && (
              <div>
                <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mb-3">{t("home.latest_results")}</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {recent.map((g) => <GameCard key={g.id} game={g} teamsMap={teamsMap} />)}
                </div>
              </div>
            )}
            {gamesF.length === 0 && (
              <div className="border border-dashed border-navy-border p-12 text-center text-ink-muted">{t("tournament.no_games")}</div>
            )}
          </TabsContent>

          <TabsContent value="teams" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {teamsF.map((team) => (
                <Link key={team.id} to={`/teams/${team.id}`} className="bg-navy-deep border border-navy-border hover:border-orange rounded-sm p-4 flex items-center gap-3" data-testid={`tourney-team-${team.id}`}>
                  <div className="w-1.5 h-12 rounded-sm shrink-0" style={{ background: team.color || "#FFFFFF" }} />
                  <div className="w-12 h-12 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
                    {team.logo_url ? <img src={fileUrl(team.logo_url)} alt="" className="w-full h-full object-cover" />
                      : <span className="font-heading font-black text-xl text-orange">{team.name?.[0]}</span>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-bold uppercase tracking-tight text-white truncate">{team.name}</div>
                    {team.category_id && (
                      <div className="text-[10px] font-heading uppercase tracking-widest text-ink-muted truncate">
                        {(categoryList.find((c) => c.id === team.category_id) || {}).name}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
              {teamsF.length === 0 && <div className="col-span-full text-ink-muted text-center p-8">{t("tournament.no_teams")}</div>}
            </div>
          </TabsContent>

          <TabsContent value="players" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {playersF.map((p) => (
                <Link key={p.id} to={`/players/${p.id}`} className="bg-navy-deep border border-navy-border hover:border-orange rounded-sm p-3 flex items-center gap-3" data-testid={`tourney-player-${p.id}`}>
                  <div className="w-12 h-12 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
                    {p.photo_url ? <img src={fileUrl(p.photo_url)} alt="" className="w-full h-full object-cover" />
                      : <span className="font-heading font-black text-orange">{p.full_name?.[0]}</span>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono tabular text-orange font-black text-lg leading-none">#{p.jersey_number}</div>
                    <div className="font-heading uppercase tracking-tight text-white text-sm truncate">{p.full_name}</div>
                    <div className="text-[10px] text-ink-muted truncate">{teamsMap[p.team_id]?.name || ""}</div>
                  </div>
                </Link>
              ))}
              {playersF.length === 0 && <div className="col-span-full text-ink-muted text-center p-8">{t("tournament.no_players")}</div>}
            </div>
          </TabsContent>

          <TabsContent value="leaders" className="mt-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { k: "pts", l: "PTS" }, { k: "ast", l: "AST" }, { k: "reb", l: "RT" },
                { k: "eff", l: "EF" }, { k: "fg_pct", l: "FG%" }, { k: "tov", l: "TO" }, { k: "stl", l: "STL" },
              ].map((m) => (
                <button
                  key={m.k}
                  data-testid={`tourney-metric-${m.k}`}
                  onClick={() => setMetric(m.k)}
                  className={`px-3 py-1.5 text-xs font-heading uppercase tracking-widest rounded-sm border transition-colors ${
                    metric === m.k ? "bg-orange border-orange text-white" : "bg-transparent border-navy-border text-ink-secondary hover:text-white hover:border-orange"
                  }`}
                >
                  {m.l}
                </button>
              ))}
            </div>
            {leaders.length === 0 ? (
              <div className="border border-dashed border-navy-border p-10 text-center text-ink-muted">{t("tournament.no_leaders")}</div>
            ) : (
              <div className="border border-navy-border rounded-sm overflow-hidden">
                {leaders.map((r, i) => (
                  <Link key={r.player.id} to={`/players/${r.player.id}`} className={`flex items-center gap-3 px-4 py-3 border-b border-navy-border hover:bg-navy-surface/40 ${i === 0 ? "bg-orange/10" : ""}`}>
                    <div className="w-8 flex items-center gap-1 font-mono tabular text-xl font-black">
                      {i === 0 && <Crown size={14} className="text-orange" />}
                      <span className={i === 0 ? "text-orange" : "text-ink-secondary"}>{i + 1}</span>
                    </div>
                    <div className="w-9 h-9 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
                      {r.player.photo_url ? <img src={fileUrl(r.player.photo_url)} alt="" className="w-full h-full object-cover" />
                        : <span className="font-heading font-bold text-orange">{r.player.full_name?.[0]}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold uppercase tracking-tight text-white text-sm truncate">{r.player.full_name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted truncate">#{r.player.jersey_number} · {r.team?.name || ""}</div>
                    </div>
                    <div className="text-right shrink-0 min-w-[64px]">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{metric.toUpperCase()}</div>
                      <div className="font-mono tabular text-xl font-black text-orange">{r.stats[metric] ?? 0}{metric === "fg_pct" ? "%" : ""}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

function GameCard({ game, teamsMap }) {
  const ta = teamsMap[game.team_a_id];
  const tb = teamsMap[game.team_b_id];
  return (
    <Link to={`/games/${game.id}`} className="group block bg-navy-deep border border-navy-border hover:border-orange transition-all rounded-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-navy-border bg-navy-surface/40">
        <StatusBadge status={game.status} />
        <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{game.date}</span>
      </div>
      <div className="p-4 space-y-2">
        <Row team={ta} score={game.score_a} highlight={game.status !== "scheduled" && game.score_a >= game.score_b} />
        <Row team={tb} score={game.score_b} highlight={game.status !== "scheduled" && game.score_b > game.score_a} />
      </div>
      <div className="px-4 pb-3 flex items-center justify-between text-xs">
        <span className="font-mono uppercase tracking-widest text-ink-muted">3×3</span>
        <span className="flex items-center gap-1 text-orange font-heading uppercase tracking-widest group-hover:translate-x-1 transition-transform">
          Box <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
}

function Row({ team, score, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-8 h-8 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
          {team?.logo_url ? <img src={fileUrl(team.logo_url)} alt="" className="w-full h-full object-cover" />
            : <span className="font-heading font-black text-orange text-xs">{team?.name?.[0] || "?"}</span>}
        </div>
        <span className={`font-heading uppercase tracking-tight truncate text-sm ${highlight ? "text-white" : "text-ink-secondary"}`}>
          {team?.name || "TBD"}
        </span>
      </div>
      <span className={`font-mono tabular text-xl ${highlight ? "text-orange font-bold" : "text-ink-secondary"}`}>{score ?? 0}</span>
    </div>
  );
}
